import { Badge, Button, Modal, Tabs } from '../../components/primitives';
import { ListPage, ListTable, type ListColumn } from '../../components/list';
import { DocBlock, DocRow, DocInlineDate } from '../../components/document';
import { StavbaGate } from '../../components/stavba';
import { useEffect, useRef, useState } from 'react';
import {
  useVady,
  useVada,
  useCreateVada,
  useUpdateVada,
  useDeleteVada,
  useVadaPhotos,
  useUploadVadaPhoto,
} from '../../api/hooks';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import type { Vada, VadaStav, Priorita } from '../../api/schemas';
import { getAuthToken } from '../../lib/api';

const stavMap: Record<VadaStav, { tone: 'yellow' | 'success' | 'danger'; label: string }> = {
  otevreno: { tone: 'danger', label: 'Otevřená' },
  v_reseni: { tone: 'yellow', label: 'V řešení' },
  uzavreno: { tone: 'success', label: 'Odstraněna' },
};

const priorityMap: Record<Priorita, { tone: 'danger' | 'yellow' | 'neutral'; label: string }> = {
  vysoka: { tone: 'danger', label: 'Vysoká' },
  stredni: { tone: 'yellow', label: 'Střední' },
  nizka: { tone: 'neutral', label: 'Nízká' },
};

const tabs = [
  { id: 'all', label: 'Vše' },
  { id: 'open', label: 'Otevřené' },
  { id: 'closed', label: 'Odstraněné' },
];

function todayCz(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}. ${mm}. ${d.getFullYear()}`;
}

function czToIso(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})$/);
  if (!m) return null;
  return `${m[3]}-${String(m[2]).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
}

/** Auth-aware fetch obrázku, ukázka v <img>. */
function PhotoThumb({ url }: { url: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    const token = getAuthToken();
    let blobUrl: string | null = null;
    let cancelled = false;
    fetch(url, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then((r) => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then((b) => {
        if (cancelled) return;
        blobUrl = URL.createObjectURL(b);
        setSrc(blobUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);
  if (!src) return null;
  return <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
}

export function VadyPage() {
  const { activeStavba } = useActiveStavba();
  const stavbaId = activeStavba?.id ?? null;
  const [tab, setTab] = useState('all');

  const filters = tab === 'open' ? { stav: 'otevreno' } : tab === 'closed' ? { stav: 'uzavreno' } : undefined;
  const { data: vady = [], isLoading, error } = useVady(stavbaId, filters);
  const create = useCreateVada(stavbaId);
  const update = useUpdateVada(stavbaId);
  const del = useDeleteVada(stavbaId);

  const [openNew, setOpenNew] = useState(false);
  const [popis, setPopis] = useState('');
  const [misto, setMisto] = useState('');
  const [priorita, setPriorita] = useState<Priorita>('stredni');
  const [odpovedna, setOdpovedna] = useState('');
  const [termin, setTermin] = useState('');
  const [datum, setDatum] = useState(todayCz());

  const [editId, setEditId] = useState<number | null>(null);
  const detailQuery = useVada(stavbaId, editId);
  const photosQuery = useVadaPhotos(stavbaId, editId);
  const uploadPhoto = useUploadVadaPhoto(stavbaId, editId);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // editovatelná kopie
  const [editDraft, setEditDraft] = useState<Vada | null>(null);
  useEffect(() => {
    if (detailQuery.data) setEditDraft(detailQuery.data);
  }, [detailQuery.data]);

  async function handleCreate() {
    if (!popis.trim()) return;
    await create.mutateAsync({
      popis: popis.trim(),
      misto: misto.trim(),
      odpovedna_osoba: odpovedna.trim(),
      termin_odstraneni: termin.trim(),
      priorita,
      datum_zjisteni: czToIso(datum) ?? undefined,
    });
    setOpenNew(false);
    setPopis('');
    setMisto('');
    setPriorita('stredni');
    setOdpovedna('');
    setTermin('');
    setDatum(todayCz());
  }

  async function handleStavChange(newStav: VadaStav) {
    if (!editDraft) return;
    await update.mutateAsync({ vadaId: editDraft.id, payload: { stav: newStav } });
  }

  async function handleSaveEdit() {
    if (!editDraft) return;
    await update.mutateAsync({
      vadaId: editDraft.id,
      payload: {
        popis: editDraft.popis,
        misto: editDraft.misto,
        odpovedna_osoba: editDraft.odpovedna_osoba,
        termin_odstraneni: editDraft.termin_odstraneni,
        priorita: editDraft.priorita,
        poznamka: editDraft.poznamka ?? '',
      },
    });
  }

  async function handleDelete() {
    if (!editDraft) return;
    if (!window.confirm(`Opravdu smazat vadu V-${editDraft.cislo}?`)) return;
    await del.mutateAsync(editDraft.id);
    setEditId(null);
  }

  async function handlePhotoUpload(files: FileList | null) {
    if (!files || !editDraft) return;
    for (const f of Array.from(files)) {
      try {
        await uploadPhoto.mutateAsync(f);
      } catch {}
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  async function openPhoto(id: number) {
    if (!stavbaId) return;
    const token = getAuthToken();
    const r = await fetch(
      `/api/stavby/${stavbaId}/photos/${id}/full`,
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
    );
    if (!r.ok) return;
    const b = await r.blob();
    window.open(URL.createObjectURL(b), '_blank');
  }

  const columns: ListColumn<Vada>[] = [
    { key: 'cislo', label: 'Č.', width: '80px', mono: true, dim: true, render: (v) => `V-${v.cislo}` },
    { key: 'popis', label: 'Popis', primary: true },
    { key: 'misto', label: 'Místo', width: '200px', dim: true, render: (v) => v.misto || '—' },
    { key: 'priorita', label: 'Priorita', width: '110px', render: (v) => <Badge tone={priorityMap[v.priorita]?.tone ?? 'neutral'} pill>{priorityMap[v.priorita]?.label ?? v.priorita}</Badge> },
    { key: 'stav', label: 'Stav', width: '140px', render: (v) => <Badge tone={stavMap[v.stav]?.tone ?? 'neutral'} pill dot>{stavMap[v.stav]?.label ?? v.stav}</Badge> },
    { key: 'datum_zjisteni', label: 'Zjištěno', width: '120px', mono: true, dim: true, render: (v) => v.datum_zjisteni ? v.datum_zjisteni.slice(0, 10) : '—' },
    { key: 'termin_odstraneni', label: 'Termín', width: '120px', mono: true, dim: true, render: (v) => v.termin_odstraneni || '—' },
    { key: 'photos_count', label: 'Foto', width: '60px', dim: true, render: (v) => v.photos_count > 0 ? `📷 ${v.photos_count}` : '' },
  ];

  if (!activeStavba) {
    return (
      <ListPage breadcrumb={['Realizace', 'Vady a nedodělky']} title="Vady a nedodělky">
        <StavbaGate message="Vady a nedodělky se evidují k jedné stavbě." />
      </ListPage>
    );
  }

  const photos = photosQuery.data ?? [];

  return (
    <>
      <ListPage
        breadcrumb={['Realizace', activeStavba.nazev, 'Vady a nedodělky']}
        title="Vady a nedodělky"
        subtitle={isLoading ? 'Načítám…' : error ? 'Chyba' : `${vady.length} záznamů`}
        status={isLoading ? 'neutral' : error ? 'draft' : 'done'}
        statusText={isLoading ? 'Načítám…' : error ? 'Chyba' : `${vady.length} záznamů`}
        actions={<Button variant="primary" onClick={() => setOpenNew(true)}>+ Nová vada</Button>}
        searchPlaceholder="Hledat vadu…"
        tools={<Tabs items={tabs} value={tab} onChange={setTab} />}
      >
        {error ? (
          <div style={{ padding: 24, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)' }}>
            Chyba: {error instanceof Error ? error.message : 'neznámá'}
          </div>
        ) : (
          <ListTable
            columns={columns}
            rows={vady}
            rowKey={(v) => v.id}
            onRowClick={(v) => setEditId(v.id)}
            emptyTitle="Žádné vady"
            emptyText="Zaznamenané vady se objeví zde."
          />
        )}
      </ListPage>

      {/* Vytvořit */}
      <Modal
        open={openNew}
        onClose={() => setOpenNew(false)}
        title="Nová vada"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenNew(false)}>Zrušit</Button>
            <Button variant="primary" onClick={handleCreate} disabled={create.isPending || !popis.trim()}>
              {create.isPending ? 'Ukládám…' : 'Vytvořit'}
            </Button>
          </>
        }
      >
        <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
          <DocBlock idx="01" title="Popis vady">
            <DocRow k="Popis *" multiline value={popis} onChange={setPopis} placeholder="Trhlina v omítce jihozápadní stěny…" />
            <DocRow k="Místo" value={misto} onChange={setMisto} placeholder="chodba 1.NP, pokoj 2.13…" />
            <DocRow
              k="Priorita"
              v={
                <select
                  value={priorita}
                  onChange={(e) => setPriorita(e.target.value as Priorita)}
                  style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  aria-label="Priorita"
                >
                  <option value="nizka">Nízká</option>
                  <option value="stredni">Střední</option>
                  <option value="vysoka">Vysoká</option>
                </select>
              }
            />
            <DocRow k="Odpovědná osoba" value={odpovedna} onChange={setOdpovedna} placeholder="Jméno subdodavatele / profese" />
            <DocRow k="Termín odstranění" mono value={termin} onChange={setTermin} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={termin} onChange={setTermin} />} />
            <DocRow k="Datum zjištění" mono value={datum} onChange={setDatum} placeholder="dd. mm. rrrr" actions={<DocInlineDate value={datum} onChange={setDatum} />} />
          </DocBlock>
        </div>
        {create.isError && (
          <div style={{ marginTop: 12, padding: 12, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 13 }}>
            Uložení selhalo: {create.error instanceof Error ? create.error.message : 'neznámá chyba'}
          </div>
        )}
      </Modal>

      {/* Detail/Edit */}
      <Modal
        open={!!editId}
        onClose={() => setEditId(null)}
        title={editDraft ? `Vada V-${editDraft.cislo}` : 'Vada'}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditId(null)}>Zavřít</Button>
            <Button variant="danger" onClick={handleDelete} disabled={del.isPending || !editDraft}>
              {del.isPending ? 'Mažu…' : '🗑️ Smazat'}
            </Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={update.isPending || !editDraft}>
              {update.isPending ? 'Ukládám…' : 'Uložit změny'}
            </Button>
          </>
        }
      >
        {detailQuery.isLoading || !editDraft ? (
          <div style={{ padding: 20, color: 'var(--sd-doc-dim)' }}>Načítám…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Status switcher */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--sd-text-dim)', textTransform: 'uppercase' }}>Stav:</span>
              {(['otevreno', 'v_reseni', 'uzavreno'] as VadaStav[]).map((st) => (
                <Button
                  key={st}
                  size="sm"
                  variant={editDraft.stav === st ? 'primary' : 'secondary'}
                  onClick={() => handleStavChange(st)}
                  disabled={update.isPending || editDraft.stav === st}
                >
                  {stavMap[st].label}
                </Button>
              ))}
              {editDraft.datum_uzavreni && (
                <span style={{ fontSize: 11.5, color: 'var(--sd-text-dimmer)' }}>
                  · uzavřeno {new Date(editDraft.datum_uzavreni).toLocaleDateString('cs-CZ')}
                </span>
              )}
            </div>

            <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', overflow: 'hidden' }}>
              <DocBlock idx="01" title="Detail vady">
                <DocRow k="Popis" multiline value={editDraft.popis} onChange={(v) => setEditDraft({ ...editDraft, popis: v })} />
                <DocRow k="Místo" value={editDraft.misto} onChange={(v) => setEditDraft({ ...editDraft, misto: v })} />
                <DocRow
                  k="Priorita"
                  v={
                    <select
                      value={editDraft.priorita}
                      onChange={(e) => setEditDraft({ ...editDraft, priorita: e.target.value as Priorita })}
                      style={{ padding: '3px 6px', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', color: 'var(--sd-doc-ink)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      <option value="nizka">Nízká</option>
                      <option value="stredni">Střední</option>
                      <option value="vysoka">Vysoká</option>
                    </select>
                  }
                />
                <DocRow k="Odpovědná osoba" value={editDraft.odpovedna_osoba} onChange={(v) => setEditDraft({ ...editDraft, odpovedna_osoba: v })} />
                <DocRow k="Termín odstranění" mono value={editDraft.termin_odstraneni} onChange={(v) => setEditDraft({ ...editDraft, termin_odstraneni: v })} actions={<DocInlineDate value={editDraft.termin_odstraneni} onChange={(v) => setEditDraft({ ...editDraft, termin_odstraneni: v })} />} />
                <DocRow k="Poznámka" multiline rows={3} value={editDraft.poznamka ?? ''} onChange={(v) => setEditDraft({ ...editDraft, poznamka: v })} placeholder="interní poznámka, postup řešení…" />
              </DocBlock>
            </div>

            {/* Fotky */}
            <div style={{ border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, background: '#fff', padding: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--sd-text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Fotky vady ({photos.length})
              </div>
              {photosQuery.isLoading ? (
                <div style={{ fontSize: 12, color: 'var(--sd-text-dim)' }}>Načítám fotky…</div>
              ) : photos.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--sd-text-dim)', fontStyle: 'italic' }}>Zatím žádné fotky.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
                  {photos.map((p) => (
                    <div
                      key={p.id}
                      onClick={() => openPhoto(p.id)}
                      style={{ aspectRatio: '1', background: 'var(--sd-w-f0ebe3, #f0ebe3)', border: '1px solid var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 4, overflow: 'hidden', cursor: 'pointer' }}
                      title={p.popis || p.filename}
                    >
                      {stavbaId && <PhotoThumb url={`/api/stavby/${stavbaId}/photos/${p.id}/thumbnail`} />}
                    </div>
                  ))}
                </div>
              )}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10, padding: '6px 12px', border: '1px dashed var(--sd-w-d8d4c5, #d8d4c5)', borderRadius: 6, fontSize: 12, color: 'var(--sd-text-dim)', cursor: 'pointer' }}>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={uploadPhoto.isPending}
                  onChange={(e) => handlePhotoUpload(e.target.files)}
                  style={{ display: 'none' }}
                />
                {uploadPhoto.isPending ? 'Nahrávám…' : '📷 Přidat fotky'}
              </label>
            </div>

            {update.isError && (
              <div style={{ padding: 10, borderRadius: 4, background: 'var(--sd-danger-soft)', color: 'var(--sd-danger)', fontSize: 12.5 }}>
                Chyba: {update.error instanceof Error ? update.error.message : 'neznámá'}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
